import { useEffect } from 'react';
import { getLoadedFont, loadCatalogFont, restoreUploadedFont, UPLOAD_PREFIX } from '../core/fonts/fontLoader';
import { useStudio } from '../store/studioStore';

/**
 * Keeps the loaded font binary in sync with `text.fontId`.
 *
 * Uploaded fonts are restored from IndexedDB, so a preset or a reload that references
 * one still works without asking the user to pick the file again.
 */
export function useFontLoader(): void {
  const fontId = useStudio((s) => s.doc.fontId);
  const setFont = useStudio((s) => s.setFont);
  const setFontLoading = useStudio((s) => s.setFontLoading);
  const setFontError = useStudio((s) => s.setFontError);

  useEffect(() => {
    let cancelled = false;

    const already = getLoadedFont(fontId);
    if (already) {
      setFont(already);
      setFontError(null);
      return;
    }

    setFontLoading(true);
    setFontError(null);

    const task = fontId.startsWith(UPLOAD_PREFIX)
      ? restoreUploadedFont(fontId).then((f) => {
          if (!f) throw new Error('Dieser hochgeladene Font ist nicht mehr im Cache. Bitte erneut hochladen.');
          return f;
        })
      : loadCatalogFont(fontId);

    void task
      .then((loaded) => {
        if (cancelled) return;
        setFont(loaded);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setFontError(err.message);
      })
      .finally(() => {
        if (!cancelled) setFontLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fontId, setFont, setFontLoading, setFontError]);
}
